const GITHUB_OWNER = "smartmanagerai";
const GITHUB_REPO = "SmartManager";
const DEFAULT_PUBLIC_DOWNLOAD_BASE_URL = "https://www.smartmanager.ai/api/desktop-updates";

export const DESKTOP_DOWNLOAD_FILES = {
  mac: {
    fileName: "NV-Zero-arm64.dmg",
  },
  "mac-intel": {
    fileName: "NV-Zero-x64.dmg",
  },
  windows: {
    fileName: "NV-Zero-Setup.exe",
  },
  linux: {
    fileName: "NV-Zero-x64.deb",
  },
  "linux-appimage": {
    fileName: "NV-Zero-x64.AppImage",
  },
} as const;

const DESKTOP_DOWNLOAD_ENV_KEYS = {
  mac: "DESKTOP_MAC_ARM64_DOWNLOAD_URL",
  "mac-intel": "DESKTOP_MAC_X64_DOWNLOAD_URL",
  windows: "DESKTOP_WINDOWS_DOWNLOAD_URL",
  linux: "DESKTOP_LINUX_DEB_DOWNLOAD_URL",
  "linux-appimage": "DESKTOP_LINUX_APPIMAGE_DOWNLOAD_URL",
} as const;

export type DesktopDownloadPlatform = keyof typeof DESKTOP_DOWNLOAD_FILES;
export type DesktopDownloadSource =
  | "configured_url"
  | "configured_base_url"
  | "s3_presigned"
  | "public_cdn_default"
  | "github_releases";

export type DesktopDownloadResponse = {
  downloadPath: string;
  fileName: string;
  platform: DesktopDownloadPlatform;
  target: "desktop";
};

export type HelperDownloadPlatform = "mac" | "mac-intel";

export function isDesktopDownloadPlatform(value: string | null): value is DesktopDownloadPlatform {
  return value !== null && Object.hasOwn(DESKTOP_DOWNLOAD_FILES, value);
}

export function parseHelperDownloadResponse(
  value: unknown,
  expectedPlatform: HelperDownloadPlatform,
  origin: string,
): { href: string; fileName: string } | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (
    candidate.target !== "helper"
    || candidate.platform !== expectedPlatform
    || typeof candidate.fileName !== "string"
    || candidate.fileName.length > 128
    || !/^[A-Za-z0-9._-]+\.zip$/.test(candidate.fileName)
    || typeof candidate.downloadPath !== "string"
    || candidate.downloadPath.length > 256
  ) return null;
  try {
    const expectedOrigin = new URL(origin).origin;
    const url = new URL(candidate.downloadPath, expectedOrigin);
    if (
      url.origin !== expectedOrigin
      || url.pathname !== "/api/download"
      || url.hash
      || url.searchParams.size !== 2
      || url.searchParams.get("target") !== "helper"
      || url.searchParams.get("platform") !== expectedPlatform
    ) return null;
    return { href: url.href, fileName: candidate.fileName };
  } catch {
    return null;
  }
}

/** Keeps the landing page on a same-origin route instead of trusting an external URL from JSON. */
export function parseDesktopDownloadResponse(
  value: unknown,
  expectedPlatform: DesktopDownloadPlatform,
  origin: string,
): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (
    candidate.target !== "desktop"
    || candidate.platform !== expectedPlatform
    || candidate.fileName !== DESKTOP_DOWNLOAD_FILES[expectedPlatform].fileName
    || typeof candidate.downloadPath !== "string"
    || candidate.downloadPath.length > 256
  ) return null;

  try {
    const expectedOrigin = new URL(origin).origin;
    const url = new URL(candidate.downloadPath, expectedOrigin);
    if (
      url.origin !== expectedOrigin
      || url.pathname !== "/api/download"
      || url.hash
      || url.username
      || url.password
      || url.searchParams.size !== 2
      || url.searchParams.get("target") !== "desktop"
      || url.searchParams.get("platform") !== expectedPlatform
    ) return null;
    return url.href;
  } catch {
    return null;
  }
}

function getGitHubReleaseUrl(fileName: string): string {
  const encodedFileName = encodeURIComponent(fileName);
  return `https://github.com/${GITHUB_OWNER}/${GITHUB_REPO}/releases/latest/download/${encodedFileName}`;
}

function joinDownloadBaseUrl(baseUrl: string, fileName: string): string {
  const normalizedBaseUrl = baseUrl.trim().replace(/\/+$/, "");
  return `${normalizedBaseUrl}/${encodeURIComponent(fileName)}`;
}

export function getDesktopDownloadUrl(platform: DesktopDownloadPlatform): string {
  const { fileName } = DESKTOP_DOWNLOAD_FILES[platform];
  const directUrl = process.env[DESKTOP_DOWNLOAD_ENV_KEYS[platform]]?.trim();
  if (directUrl) return directUrl;

  const baseUrl = process.env.DESKTOP_DOWNLOAD_BASE_URL?.trim();
  if (baseUrl) return joinDownloadBaseUrl(baseUrl, fileName);

  if (process.env.DESKTOP_GITHUB_RELEASES_PUBLIC === "true") {
    return getGitHubReleaseUrl(fileName);
  }

  return joinDownloadBaseUrl(DEFAULT_PUBLIC_DOWNLOAD_BASE_URL, fileName);
}

export function getDesktopDownloadSource(platform: DesktopDownloadPlatform): DesktopDownloadSource {
  if (process.env[DESKTOP_DOWNLOAD_ENV_KEYS[platform]]?.trim()) return "configured_url";
  if (process.env.DESKTOP_DOWNLOAD_BASE_URL?.trim()) return "configured_base_url";
  if (getDesktopDownloadS3Bucket()) return "s3_presigned";
  if (process.env.DESKTOP_GITHUB_RELEASES_PUBLIC === "true") return "github_releases";
  return "public_cdn_default";
}

export function getDesktopDownloadS3Bucket(): string | null {
  return process.env.DESKTOP_DOWNLOAD_S3_BUCKET?.trim() || process.env.AWS_BUCKET_NAME?.trim() || null;
}

export function getDesktopDownloadS3Prefix(): string {
  const runtimePrefix = process.env.DESKTOP_DOWNLOAD_S3_PREFIX?.trim().replace(/^\/+|\/+$/g, "");
  const releasePrefix = process.env.DESKTOP_RELEASE_S3_PREFIX?.trim().replace(/^\/+|\/+$/g, "");
  if (runtimePrefix && releasePrefix && runtimePrefix !== `${releasePrefix}/latest`) {
    throw new Error("DESKTOP_DOWNLOAD_S3_PREFIX must equal DESKTOP_RELEASE_S3_PREFIX plus /latest");
  }
  return runtimePrefix || (releasePrefix ? `${releasePrefix}/latest` : "downloads/desktop/latest");
}

export function getDesktopDownloadS3Key(platform: DesktopDownloadPlatform): string {
  const { fileName } = DESKTOP_DOWNLOAD_FILES[platform];
  return `${getDesktopDownloadS3Prefix()}/${fileName}`;
}

export function getDesktopUpdateS3Key(fileName: string): string {
  return `${getDesktopDownloadS3Prefix()}/${fileName}`;
}

export function isAllowedDesktopUpdateFileName(fileName: string): boolean {
  return /^(?:latest(?:-(?:mac|linux))?\.yml|desktop-release-manifest\.json|NVZeroHelper\.zip|NV-Zero-(?:arm64|x64)\.dmg|NV-Zero-Setup\.exe|NV-Zero-x64\.(?:deb|AppImage)|NV-Zero-\d+\.\d+\.\d+-(?:arm64|x64)\.(?:dmg|zip)(?:\.blockmap)?|NV-Zero-\d+\.\d+\.\d+-x64\.(?:deb|AppImage)|NV-Zero-Setup-\d+\.\d+\.\d+\.exe(?:\.blockmap)?)$/.test(fileName);
}

/**
 * The desktop release publishes the standalone macOS helper beside the
 * installers, under this fixed name. There is no per-file override: a
 * separately hosted helper drifts from the release, which is how a stale
 * private GitHub URL once broke every helper download.
 */
export const DESKTOP_HELPER_FILE_NAME = "NVZeroHelper.zip";

/** The public release feed that serves the helper, for hosts without the release bucket. */
export function getDesktopHelperDownloadUrl(): string {
  const baseUrl = process.env.DESKTOP_DOWNLOAD_BASE_URL?.trim() || DEFAULT_PUBLIC_DOWNLOAD_BASE_URL;
  return joinDownloadBaseUrl(baseUrl, DESKTOP_HELPER_FILE_NAME);
}

/** The canonical production feed URL, which development borrows the helper from. */
export function getCanonicalProductionHelperUrl(): string {
  return joinDownloadBaseUrl(DEFAULT_PUBLIC_DOWNLOAD_BASE_URL, DESKTOP_HELPER_FILE_NAME);
}
